/**
 * Sharing Settings Page
 *
 * Allows Plus users to view and manage shares they've created.
 * Shows list of active/pending shares with ability to revoke.
 *
 * @module pages/sharing-settings
 */

import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Share2,
  Users,
  Link2,
  Loader2,
  AlertCircle,
  Trash2,
  Clock,
  CheckCircle,
  Folder,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/client/lib/api';
import { formatDate } from '@/client/lib/utils';
import { SettingsLayout } from '@/client/components/layout/settings-layout';

interface Share {
  id: string;
  inviteeEmail: string;
  inviteeName: string | null;
  scopeType: 'link' | 'account' | 'folder';
  linkId: string | null;
  linkTitle: string | null;
  linkShortCode: string | null;
  folderId: string | null;
  folderName: string | null;
  folderColor: string | null;
  folderLinkCount: number | null;
  status: 'pending' | 'active' | 'revoked';
  createdAt: string;
  acceptedAt: string | null;
  expiresAt: string | null;
}

interface SharesResponse {
  shares: Share[];
}

export function SharingSettingsPage() {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery<SharesResponse>({
    queryKey: ['shares', 'created'],
    queryFn: async () => {
      const response = await apiFetch('/api/shares');
      if (!response.ok) throw new Error('Failed to fetch shares');
      return response.json();
    },
  });

  const revokeMutation = useMutation({
    mutationFn: async (shareId: string) => {
      const response = await apiFetch(`/api/shares/${shareId}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || error.error || 'Failed to revoke share');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shares'] });
      toast.success('Share revoked successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleRevoke = (shareId: string) => {
    if (
      window.confirm(
        'Are you sure you want to revoke this share? The user will no longer be able to view your analytics.'
      )
    ) {
      revokeMutation.mutate(shareId);
    }
  };

  const shares = data?.shares || [];
  const activeShares = shares.filter((s) => s.status === 'active');
  const pendingShares = shares.filter((s) => s.status === 'pending');

  return (
    <SettingsLayout>
      {/* Sharing Content */}
      <div className="space-y-6">
        <div className="rounded-lg border bg-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold">Analytics Sharing</h2>
              <p className="text-sm text-muted-foreground">
                Manage who can view your link analytics
              </p>
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-32 text-center">
              <AlertCircle className="h-8 w-8 text-destructive mb-2" />
              <p className="text-sm text-muted-foreground">Failed to load shares</p>
            </div>
          ) : shares.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="space-y-6">
              {/* Active Shares */}
              {activeShares.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium flex items-center gap-2 mb-3">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    Active Shares ({activeShares.length})
                  </h3>
                  <div className="divide-y rounded-lg border">
                    {activeShares.map((share) => (
                      <ShareRow
                        key={share.id}
                        share={share}
                        onRevoke={handleRevoke}
                        isRevoking={revokeMutation.isPending}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Pending Shares */}
              {pendingShares.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium flex items-center gap-2 mb-3">
                    <Clock className="h-4 w-4 text-amber-500" />
                    Pending Invitations ({pendingShares.length})
                  </h3>
                  <div className="divide-y rounded-lg border border-amber-500/30 bg-amber-500/5">
                    {pendingShares.map((share) => (
                      <ShareRow
                        key={share.id}
                        share={share}
                        onRevoke={handleRevoke}
                        isRevoking={revokeMutation.isPending}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    These users haven&apos;t created an account yet. They&apos;ll get access when
                    they sign up.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* How it works */}
        <div className="rounded-lg border border-dashed p-4">
          <h3 className="font-medium mb-2">How sharing works</h3>
          <ul className="text-sm text-muted-foreground space-y-1.5">
            <li className="flex items-start gap-2">
              <span className="text-primary">1.</span>
              Share from any folder&apos;s detail page using the share button
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">2.</span>
              All links in the folder are shared, including links added later
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">3.</span>
              Invited users can view analytics but cannot edit anything
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">4.</span>
              Revoke access at any time from this page
            </li>
          </ul>
        </div>
      </div>
    </SettingsLayout>
  );
}

function ShareRow({
  share,
  onRevoke,
  isRevoking,
}: {
  share: Share;
  onRevoke: (id: string) => void;
  isRevoking: boolean;
}) {
  const isExpired = share.expiresAt && new Date(share.expiresAt) < new Date();

  // Determine icon and description based on scope type
  const getIcon = () => {
    if (share.scopeType === 'folder') {
      return (
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
          style={{ backgroundColor: share.folderColor ? `${share.folderColor}20` : undefined }}
        >
          <Folder className="h-5 w-5" style={{ color: share.folderColor || undefined }} />
        </div>
      );
    }
    if (share.scopeType === 'account') {
      return (
        <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
          <Users className="h-5 w-5 text-muted-foreground" />
        </div>
      );
    }
    return (
      <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
        <Link2 className="h-5 w-5 text-muted-foreground" />
      </div>
    );
  };

  const getDescription = () => {
    if (share.scopeType === 'folder') {
      return (
        <>
          <span className="font-medium">{share.folderName}</span>
          {share.folderLinkCount !== null && (
            <span className="text-muted-foreground">
              {' '}
              ({share.folderLinkCount} {share.folderLinkCount === 1 ? 'link' : 'links'})
            </span>
          )}
        </>
      );
    }
    if (share.scopeType === 'account') {
      return 'All links (full dashboard access)';
    }
    return share.linkTitle || `arml.ink/${share.linkShortCode}`;
  };

  return (
    <div className="flex items-center gap-4 p-4">
      {getIcon()}

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-medium truncate">{share.inviteeName || share.inviteeEmail}</p>
          {share.status === 'pending' && (
            <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-amber-500/10 text-amber-600">
              Pending
            </span>
          )}
          {isExpired && (
            <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-destructive/10 text-destructive">
              Expired
            </span>
          )}
        </div>
        <p className="text-sm text-muted-foreground truncate">{getDescription()}</p>
        <p className="text-xs text-muted-foreground mt-0.5">
          Shared {formatDate(share.createdAt)}
          {share.expiresAt && !isExpired && ` · Expires ${formatDate(share.expiresAt)}`}
        </p>
      </div>

      <button
        onClick={() => onRevoke(share.id)}
        disabled={isRevoking}
        className="p-2 rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50"
        title="Revoke access"
      >
        {isRevoking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
      </button>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-8">
      <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-muted flex items-center justify-center">
        <Share2 className="h-6 w-6 text-muted-foreground" />
      </div>
      <h3 className="font-medium mb-1">No shares yet</h3>
      <p className="text-sm text-muted-foreground max-w-sm mx-auto">
        Share link analytics with teammates or stakeholders from any link&apos;s detail page.
      </p>
      <Link
        to="/links"
        className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
      >
        <Link2 className="h-4 w-4" />
        View Your Links
      </Link>
    </div>
  );
}
